import { Star } from "lucide-react";
import { formaatScore } from "@/data/reviews";
import { useReviewStats } from "@/hooks/use-review-stats";

const AMBER = "#fbb929";
const NACHTBLAUW = "#13233f";
const GRIJS = "#4a5a73";

function Stars({ rating5, size = "md" }: { rating5: number; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <div
      className="flex items-center gap-0.5"
      role="img"
      aria-label={`${formaatScore(rating5)} van 5 sterren`}
    >
      {[0, 1, 2, 3, 4].map((i) => {
        const fillPct = Math.max(0, Math.min(1, rating5 - i)) * 100;
        return (
          <span key={i} className={`relative inline-block ${dim}`}>
            <Star className={`${dim}`} style={{ color: `${AMBER}40` }} aria-hidden="true" />
            {fillPct > 0 && (
              <span
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${fillPct}%` }}
                aria-hidden="true"
              >
                <Star className={`${dim}`} style={{ color: AMBER, fill: AMBER }} />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Grote reviewbadge voor de homepage: totaalscore links, per bron een regel
 * met voortgangsbalkje rechts. Op mobiel stapelen de bronnen onder het
 * grote cijfer. Bewust géén JSON-LD: reviews over het eigen bedrijf op de
 * eigen site zijn self-serving en leveren geen sterren in Google op.
 */
export function ReviewBadge() {
  const { totaal, bronnen, bijgewerkt } = useReviewStats();
  const sterren = (totaal.score / totaal.schaal) * 5;

  return (
    <section aria-label="Klantbeoordelingen" className="bg-[#f4f6fa]">
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-8 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-black/5 md:flex-row md:items-center md:gap-10 md:p-10">
          <div className="flex-1">
            <p
              className="text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: GRIJS }}
            >
              Elvion Group Nederland · Wageningen
            </p>
            <div className="mt-3 flex items-end gap-2">
              <span
                className="font-mono text-6xl font-bold leading-none"
                style={{ color: NACHTBLAUW }}
              >
                {formaatScore(totaal.score)}
              </span>
              <span className="font-mono text-xl" style={{ color: GRIJS }}>
                /{totaal.schaal}
              </span>
            </div>
            <div className="mt-3">
              <Stars rating5={sterren} />
            </div>
            <p className="mt-2 text-sm" style={{ color: GRIJS }}>
              op basis van {totaal.aantal} reviews
            </p>
          </div>

          <div
            className="h-px w-full md:h-auto md:w-px md:self-stretch"
            style={{ backgroundColor: `${NACHTBLAUW}1a` }}
          />

          <div className="flex-1 space-y-5">
            {bronnen.map((bron) => {
              const pct = Math.round((bron.score / bron.schaal) * 100);
              return (
                <div key={bron.naam}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <a
                      href={bron.url}
                      target="_blank"
                      rel="noopener"
                      className="text-sm font-semibold underline-offset-4 hover:underline"
                      style={{ color: NACHTBLAUW }}
                    >
                      {bron.naam}
                    </a>
                    <span className="font-mono text-sm" style={{ color: NACHTBLAUW }}>
                      {formaatScore(bron.score)}/{bron.schaal}{" "}
                      <span style={{ color: GRIJS }}>· {bron.aantal} reviews</span>
                    </span>
                  </div>
                  <div
                    className="mt-2 h-1.5 w-full rounded-full"
                    style={{ backgroundColor: `${NACHTBLAUW}1a` }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, backgroundColor: AMBER }}
                    />
                  </div>
                </div>
              );
            })}
            <p className="text-xs" style={{ color: GRIJS }}>
              Bijgewerkt: {bijgewerkt}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Compacte variant voor de footer (donkere achtergrond). */
export function ReviewBadgeCompact() {
  const { totaal } = useReviewStats();
  const sterren = (totaal.score / totaal.schaal) * 5;
  return (
    <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/80">
      <span className="font-mono font-bold text-white">{formaatScore(totaal.score)}</span>
      <Stars rating5={sterren} size="sm" />
      <span>
        {totaal.aantal} reviews · Homedeal en Google
      </span>
    </div>
  );
}
